import { pushAnalyticsEvent } from '../../utils/analytics'
import { fetchWithTimeout } from '../../utils/fetchWithTimeout'

interface NewsletterSignupPayload {
  email: string
}

class NewsletterForm {
  private readonly widgets: NodeListOf<HTMLElement>

  constructor() {
    this.widgets = document.querySelectorAll<HTMLElement>('.js-newsletter-form-widget')

    if (this.widgets.length === 0) return

    this.widgets.forEach((widget) => {
      this.setupWidget(widget)
    })
  }

  private setupWidget (widget: HTMLElement): void {
    const form = widget.querySelector<HTMLFormElement>('.js-newsletter-form')
    if (form == null) return

    const emailInput = form.querySelector<HTMLInputElement>('.js-newsletter-form-email-input')
    const successEl = widget.querySelector<HTMLElement>('.js-newsletter-form-success')
    const errorEl = widget.querySelector<HTMLElement>('.js-newsletter-form-error')
    const privacyEl = widget.querySelector<HTMLElement>('.js-newsletter-form-privacy')
    const announcer = widget.querySelector<HTMLElement>('.js-newsletter-form-announcer')

    announcer?.setAttribute('aria-atomic', 'true')

    let hasTrackedFormStart = false
    const trackFormStart = (): void => {
      if (hasTrackedFormStart) return
      hasTrackedFormStart = true
      pushAnalyticsEvent({ event: 'newsletter_form_start' })
    }
    emailInput?.addEventListener('focus', trackFormStart, { once: true })

    emailInput?.addEventListener('input', () => {
      if (emailInput.checkValidity()) {
        this.renderEmailValidationError(form, false)
      }
    })

    form.addEventListener('submit', (event) => {
      event.preventDefault()
      void this.handleSubmit(
        widget,
        form,
        emailInput,
        successEl,
        errorEl,
        privacyEl,
        announcer
      )
    })
  }

  private isEmailInvalid (form: HTMLFormElement): boolean {
    const emailInput = form.querySelector<HTMLInputElement>('.js-newsletter-form-email-input')
    if (emailInput == null) return false

    return !emailInput.checkValidity()
  }

  private renderEmailValidationError (form: HTMLFormElement, invalid: boolean): void {
    const emailInput = form.querySelector<HTMLInputElement>('.js-newsletter-form-email-input')
    const errorEl = form.querySelector<HTMLElement>('.js-newsletter-form-email-error')
    if (errorEl == null) return

    if (invalid) {
      errorEl.removeAttribute('hidden')
      emailInput?.classList.add('invalid')
      emailInput?.setAttribute('aria-invalid', 'true')
    } else {
      errorEl.setAttribute('hidden', '')
      emailInput?.classList.remove('invalid')
      emailInput?.removeAttribute('aria-invalid')
    }
  }

  private announce(announcer: HTMLElement | null, message: string): void {
    if (announcer == null) return

    announcer.textContent = ''
    void announcer.offsetHeight
    announcer.textContent = message
  }

  private setLoading(form: HTMLFormElement, isLoading: boolean): void {
    const emailInput = form.querySelector<HTMLInputElement>('.js-newsletter-form-email-input')
    const submitButton = form.querySelector<HTMLButtonElement>('.js-newsletter-form-submit')

    if (emailInput != null) emailInput.disabled = isLoading

    if (submitButton != null) {
      submitButton.disabled = isLoading
      submitButton.classList.toggle('is-loading', isLoading)
    }
  }

  private buildPayload(emailInput: HTMLInputElement | null): NewsletterSignupPayload {
    return {
      email: emailInput?.value.trim() ?? ''
    }
  }

  private async handleSubmit(
    widget: HTMLElement,
    form: HTMLFormElement,
    emailInput: HTMLInputElement | null,
    successEl: HTMLElement | null,
    errorEl: HTMLElement | null,
    privacyEl: HTMLElement | null,
    announcer: HTMLElement | null
  ): Promise<void> {
    const emailInvalid = this.isEmailInvalid(form)
    this.renderEmailValidationError(form, emailInvalid)

    if (emailInvalid) {
      pushAnalyticsEvent({
        event: 'newsletter_form_validation_error',
        invalid_email: emailInvalid
      })
    }

    if (emailInvalid) {
      emailInput?.focus()
      return
    }

    const payload = this.buildPayload(emailInput)

    this.setLoading(form, true)
    this.announce(announcer, 'Signing you up…')
    pushAnalyticsEvent({ event: 'newsletter_form_submit' })

    try {
      const response = await fetchWithTimeout('/api/newsletter/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (!response.ok) throw new Error(`Unexpected status ${response.status}`)

      errorEl?.setAttribute('hidden', '')
      form.setAttribute('hidden', '')
      privacyEl?.setAttribute('hidden', '')
      successEl?.removeAttribute('hidden')
      successEl?.focus()
      this.announce(announcer, "Thank you! You've been signed up.")
      pushAnalyticsEvent({ event: 'newsletter_signup' })
    } catch {
      successEl?.setAttribute('hidden', '')
      errorEl?.removeAttribute('hidden')
      errorEl?.focus()
      form.removeAttribute('hidden')
      privacyEl?.removeAttribute('hidden')
      this.announce(announcer, 'Signup failed. Please refresh and try again.')
      pushAnalyticsEvent({ event: 'newsletter_form_error' })
    } finally {
      this.setLoading(form, false)
    }
  }
}

export default NewsletterForm
